// HM-305: Button primario/secondario/ghost palette warm.
// Pattern shadcn-style con cva. CTA arancio per primario.

import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../lib/cn';

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium',
    'transition-all duration-200 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2',
    'focus-visible:ring-offset-bg-base',
    'disabled:pointer-events-none disabled:opacity-50',
    // touch target Apple HIG: min 44px in size lg/default; 32px sm
  ),
  {
    variants: {
      variant: {
        primary:
          'bg-accent-primary text-text-on-accent hover:bg-accent-primary-hover shadow-hm-sm hover:scale-[1.02]',
        secondary: 'bg-bg-card text-text-primary border border-border-base hover:bg-bg-muted',
        ghost: 'text-text-primary hover:bg-bg-muted',
        link: 'text-accent-primary-text hover:underline underline-offset-2',
        danger: 'bg-state-guas text-text-on-accent hover:opacity-90 shadow-hm-sm',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-11 px-4',
        lg: 'h-12 px-6 text-base',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export { buttonVariants };
